/** Public marketing + AE collateral copy. Keep claims conservative and dated. */

export const BRAND = {
  name: 'Orca AI',
  product: 'Orca AI — Fusion HCM Security & Approvals',
  short: 'Fusion HCM Security & Approvals',
  category:
    'Copilot for Oracle Fusion HCM security, SoD, access reviews, and BPM approvals.',
  notThis:
    'Not a cloud CNAPP. Not affiliated with Orca Security, the cloud-security vendor.',
  company: 'SLP Microsystems',
  partnerLine: 'Oracle Service Partner since 1999',
  siteUrl: 'https://www.slpmicrosystems.com',
  contactPath: 'https://www.slpmicrosystems.com',
  preferredHost: 'orca-ai.slpmicrosystems.com',
  currentHostNote:
    'Public hostname should be orca-ai.slpmicrosystems.com (or approvals.slpmicrosystems.com). Avoid orca-security.* — it collides with the unrelated CNAPP brand.',
  lastUpdated: 'August 21, 2026',
};

export const JOBS = [
  {
    id: 'access',
    title: 'Why can’t Jane see Compensation?',
    buyer: 'HCM security admin / HRIS',
    outcome: 'Walk roles → privileges → data security → AOR and name the smallest fix.',
    story:
      'Look up the Fusion username, open the access map, compare to a peer, and hand the ticket a concrete grant—not another Security Console tour.',
  },
  {
    id: 'approvals',
    title: 'Promotions over 5% need VP + CFO',
    buyer: 'HCM / BPM lead',
    outcome: 'Plain English → approval rule, simulate on a real worker, save a draft before Workflow Composer.',
    story:
      'Describe the policy, review the path on canvas, simulate against a person number, then deploy only when you mean to write to Fusion.',
  },
  {
    id: 'sod',
    title: 'Who can hire and run payroll?',
    buyer: 'Internal audit / security',
    outcome: 'Privilege-level SoD check, then open an access review and revoke in one place.',
    story:
      'Run the user against HCM SoD rules, flag toxic combinations, generate a campaign, approve or revoke, and sync the revoke back to Oracle.',
  },
];

export const SKUS = [
  {
    id: 'security',
    name: 'Orca AI Security',
    unit: 'Per Fusion pod / year',
    includes: [
      'Security Copilot',
      'User Lookup + access map + compare',
      'SoD rules and user checks',
      'Audit alerts',
    ],
    bestFor: 'Security admins and internal audit on a single HCM pod.',
  },
  {
    id: 'approvals',
    name: 'Orca AI Approvals',
    unit: 'Per Fusion pod / year',
    includes: [
      'Approval Copilot',
      'Approval Designer + simulate',
      'Draft save and explicit Fusion deploy',
    ],
    bestFor: 'HCM teams who live in BPM / Workflow Composer.',
  },
  {
    id: 'bundle',
    name: 'Orca AI Bundle',
    unit: 'Per Fusion pod / year',
    includes: [
      'Everything in Security and Approvals',
      'User access review campaigns + revoke sync',
    ],
    bestFor: 'SLP-delivered HCM accounts that want one day-two workbench.',
    featured: true,
  },
];

export const PRICING_NOTES = [
  'Price unit is always one Oracle Fusion HCM pod (one host), billed annually.',
  'Implementation / enablement is a separate one-time line (connection, privileges, first ruleset, AE walkthrough).',
  'Bands are quoted per customer. We do not publish a fake “from $X” — ask for the current mid-market band.',
  'This is not Pathlock / Saviynt / Oracle Advanced Controls pricing. It is a specialist HCM tool, not an enterprise GRC ELA.',
  'Ask Data (natural-language HCM query) is included for convenience on this SKU; the product story is Security & Approvals, not CloudFish.',
];

export const COMPETITORS = [
  {
    name: 'Fusion Security Console',
    they: 'Click through roles, privileges, data security, and AOR by hand.',
    we: 'One lookup, a grant map, a compare, and a smallest-fix answer.',
  },
  {
    name: 'Oracle 26C HCM Data Security Assistant',
    they: 'Strong inside Fusion for profiles, quick actions, and ACL regenerate (when licensed and published).',
    we: 'Can invoke that agent when the pod has it — and still own SoD, UAR, approvals, and the workflow around the answer.',
  },
  {
    name: 'Pathlock / Saviynt / Oracle Advanced Controls',
    they: 'Cross-app GRC, large rulesets, long services, six-figure programs.',
    we: 'HCM-only, consultant-speed, services-priced. We do not claim to replace IGA.',
  },
  {
    name: 'Generic ChatGPT',
    they: 'No Fusion grounding; will invent privileges and pages.',
    we: 'SCIM effective roles, BIP SQL, BPM simulate, and optional revoke back to Oracle.',
  },
];

export const FAQ = [
  {
    q: 'Is this Orca Security, the CNAPP company?',
    a: 'No. Orca AI is an SLP Microsystems product for Oracle Fusion HCM. We are not affiliated with Orca Security.',
  },
  {
    q: 'How is this different from Oracle’s 26C Data Security Assistant?',
    a: 'Oracle’s assistant diagnoses HCM data security inside Fusion (roles, profiles, ACLs). Orca adds SoD, access-review campaigns, audit alerts, and BPM approval design. On 26C+ pods we can call their published agent via invokeAsync and show the answer in Orca.',
  },
  {
    q: 'Does Orca write to my Fusion pod?',
    a: 'Read paths are the default (lookup, map, simulate, SoD). Writes are explicit: approval draft deploy and UAR revoke sync. ACL regenerate via Oracle’s assistant must stay a deliberate admin action.',
  },
  {
    q: 'Where does employee and security data go?',
    a: 'Fusion stays on your pod. Orca stores users, encrypted connection records, campaigns, SoD rules, and audit of prompts. LLM calls go to the provider configured for that deployment (OCI Generative AI when configured; otherwise the configured chat provider). See Trust & data flow.',
  },
  {
    q: 'Do you send HCM data to DeepSeek?',
    a: 'Only if that deployment’s LLM_PROVIDER / Ask Data provider is DeepSeek. For healthcare, financial services, and public sector we recommend OCI Generative AI in the customer’s region and will say so on the order form. We will not claim “data stays in OCI” unless OCI is actually configured.',
  },
  {
    q: 'Can we SSO with Okta or Entra ID?',
    a: 'Yes, via WorkOS AuthKit / SAML / OIDC when WORKOS_ENABLED is set for the deployment. SSO signs into Orca; Fusion access still uses a Connections integration user.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — request a free trial on your non-prod Fusion pod (or an SLP sandbox). We connect Security Copilot, User Lookup, SoD, and Access Review in the browser. No SLP admin-approval account is required just to start. Use Start free trial.',
  },
  {
    q: 'What privileges does the integration user need?',
    a: 'See the enablement one-pager: SCIM Users, HCM REST workers, BIP catalog/run for the SQL runner report, and BPM metadata for approvals. Least privilege; no human password sharing.',
  },
  {
    q: 'Do you have SOC 2?',
    a: 'SOC 2 is not published yet. Controls in place: session cookies, Mongo-backed sessions, encrypted connection secrets at rest, optional SSO, prompt audit log. Ask for the current questionnaire answers.',
  },
];

export const SUBPROCESSORS = [
  { name: 'Salesforce Heroku', role: 'Application hosting', region: 'As deployed (typically US)' },
  { name: 'MongoDB Atlas', role: 'Application data, sessions, encrypted connections', region: 'As configured' },
  { name: 'OCI Generative AI (when enabled)', role: 'Chat / embeddings for copilots', region: 'Customer OCI region (e.g. us-chicago-1)' },
  { name: 'DeepSeek (only if configured)', role: 'Chat for Ask Data / copilots', region: 'Vendor cloud (api.deepseek.com)' },
  { name: 'WorkOS (when enabled)', role: 'Enterprise SSO', region: 'Vendor cloud' },
  { name: 'Customer Oracle Fusion HCM', role: 'System of record — not a subprocessor of Orca', region: 'Customer Fusion pod' },
];

export const MEASURE = {
  label: 'What we measure (publish a named logo after the first paid pod)',
  items: [
    'Time to diagnose “why can’t they see this?” tickets',
    'Hours to draft a BPM approval rule vs Workflow Composer only',
    'SoD review prep time before a quarterly access cert',
  ],
  illustrative:
    'Illustrative SLP delivery pattern — not a named customer claim: access tickets that used to take a Security Console afternoon compress to a single lookup + map when the integration user is correctly privileged. Replace this sentence with your first measured pod.',
};

export const DEMO_STORIES = [
  {
    id: '1',
    minutes: '8–10',
    title: 'Jane can’t see Compensation Workbench',
    steps: [
      'User Lookup → Jane’s Fusion username',
      'Load access map (roles, privileges, data scope)',
      'Optional: compare to a peer who can see the page',
      'Security Copilot: state the blocking grant and smallest fix',
    ],
  },
  {
    id: '2',
    minutes: '8–10',
    title: 'Promotion over grade max + 5% → VP HR and CFO',
    steps: [
      'Approval Copilot: paste the policy in English',
      'Open Approval Designer canvas',
      'Simulate against a person number',
      'Save draft — do not deploy on a first demo unless asked',
    ],
  },
  {
    id: '3',
    minutes: '6–8',
    title: 'Hire + process payroll SoD → review → revoke',
    steps: [
      'SoD Rules: show seed + custom privilege pairs',
      'Check a username',
      'Access Review: generate campaign from the hit',
      'Approve or revoke; mention sync to Oracle is explicit',
    ],
  },
];

export const DECK_SLIDES = [
  { title: 'The job', body: 'Fusion HCM security and approvals are still tribal knowledge. Tickets sit in Security Console and Workflow Composer.' },
  { title: 'Who we are', body: 'Orca AI — Fusion HCM Security & Approvals. SLP Microsystems, Oracle Service Partner since 1999. Not a CNAPP.' },
  { title: 'Three jobs', body: 'Diagnose access. Design BPM rules. Catch HCM SoD and run a review.' },
  { title: 'How it sits', body: 'Orca beside the customer Fusion pod: SCIM, BIP SOAP, HCM REST, BPM. LLM is a configured gateway (OCI preferred).' },
  { title: 'Security story', body: 'Your pod holds HCM data. We store connections encrypted. Writes are explicit. SSO optional via WorkOS.' },
  { title: 'Competitive frame', body: 'Faster than Console. Complements 26C assistant. Not Pathlock. Not ChatGPT.' },
  { title: 'Packaging', body: 'Security / Approvals / Bundle. Unit = one Fusion pod / year + implementation.' },
  { title: 'Ask', body: 'Book a 20-minute demo on your non-prod pod. One integration user. Three canned stories.' },
];

export const ENABLEMENT = {
  connection: [
    'Fusion host only (https://xxx.fa.*.oraclecloud.com) — no /hcmRestApi path',
    'Integration user + password stored encrypted (HCM_ENCRYPTION_KEY)',
    'Test connection from Admin → Connections, then Activate',
  ],
  privileges: [
    'SCIM /hcmRestApi/scim/Users — effective roles',
    'HCM REST publicWorkers / userAccounts (fallback)',
    'BIP PublicReportService: catalog + run for the SQL runner report path',
    'BPM / AMX metadata for configured approvals and simulate',
    'Optional: FND audit BIP path for Audit Alerts',
  ],
  llm: [
    'Preferred: LLM_PROVIDER=oci with tenancy/user/fingerprint/key/region/compartment',
    'Fallback: DeepSeek only when the customer accepts that data path',
    'Never tell a regulated buyer “data stays in OCI” unless OCI is configured',
  ],
  sso: [
    'WORKOS_ENABLED + API key + client id + redirect URI',
    'SSO is Orca login only; Fusion still uses Connections',
  ],
};

export const NAV = [
  { href: '/', label: 'Product' },
  { href: '/jobs', label: 'Use cases' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/compare', label: 'Compare' },
  { href: '/trust', label: 'Trust' },
  { href: '/faq', label: 'FAQ' },
  { href: '/demo', label: 'Book a demo' },
];

export const CTA = {
  open: 'Open Orca AI',
  trial: 'Start free trial',
  demo: 'See it on your pod',
};

export const HERO = {
  kicker: 'Fusion HCM security copilot',
  headline: "Jane can’t see Compensation. Orca can tell you why.",
  body:
    'Ask in English. Get the blocking policy. Draft the approval. Catch hire-and-payroll before audit does — in the browser, on your Fusion pod. No Security Console scavenger hunt. No GRC circus.',
  suiteNote: 'From SLP Microsystems — same suite as CloudFish, TideSync, and OraConvert. This product is Orca.',
};

export const FEATURE_CARDS = [
  { title: 'Ask', sub: 'Why can’t they see the page?' },
  { title: 'Map', sub: 'The grant. The smallest fix.' },
  { title: 'Rule', sub: 'Type it. Simulate it.' },
  { title: 'Revoke', sub: 'SoD back to Fusion today.' },
];

export const CAN_DO = [
  {
    title: 'Security Copilot',
    do: '“Why can’t Jane see Compensation?” — answered.',
  },
  {
    title: 'User Lookup',
    do: 'The map. The smallest fix. Done.',
  },
  {
    title: 'Approval Copilot',
    do: '“Over 5% goes to VP HR.” Typed, not XPath’d.',
  },
  {
    title: 'Approval Designer',
    do: 'Simulate first. Write Fusion last.',
  },
  {
    title: 'SoD Rules',
    do: 'Hire + payroll in one person? Flagged.',
  },
  {
    title: 'Access Review',
    do: 'Revoke. Sync. Close the campaign.',
  },
];

export const LEGAL_NAV = [
  { href: '/privacy', label: 'Privacy' },
  { href: '/terms', label: 'Terms' },
  { href: '/dpa', label: 'DPA' },
  { href: '/trust', label: 'Data flow' },
  { href: '/subprocessors', label: 'Subprocessors' },
];

export const AE_NAV = [
  { href: '/collateral/one-pager', label: 'One-pager' },
  { href: '/collateral/demo-script', label: 'Demo script' },
  { href: '/collateral/enablement', label: 'Enablement' },
  { href: '/collateral/deck', label: '8-slide deck' },
  { href: '/collateral/video-script', label: 'Video script' },
];

export const MARKETING_PATHS = new Set([
  '/',
  '/jobs',
  '/pricing',
  '/compare',
  '/faq',
  '/trust',
  '/privacy',
  '/terms',
  '/dpa',
  '/subprocessors',
  '/demo',
  '/trial',
  '/demo-tour',
  '/collateral',
  '/collateral/one-pager',
  '/collateral/demo-script',
  '/collateral/enablement',
  '/collateral/deck',
  '/collateral/video-script',
]);

export function isMarketingPath(path) {
  return MARKETING_PATHS.has(path);
}

export function pageTitle(path) {
  const map = {
    '/': BRAND.product,
    '/jobs': `Use cases · ${BRAND.name}`,
    '/pricing': `Pricing · ${BRAND.name}`,
    '/compare': `Compare · ${BRAND.name}`,
    '/faq': `FAQ · ${BRAND.name}`,
    '/trust': `Trust & data flow · ${BRAND.name}`,
    '/privacy': `Privacy · ${BRAND.name}`,
    '/terms': `Terms · ${BRAND.name}`,
    '/dpa': `DPA · ${BRAND.name}`,
    '/subprocessors': `Subprocessors · ${BRAND.name}`,
    '/demo': `Book a demo · ${BRAND.name}`,
    '/trial': `Start free trial · ${BRAND.name}`,
    '/demo-tour': `Guided demo · ${BRAND.name}`,
    '/collateral': `AE kit · ${BRAND.name}`,
    '/collateral/one-pager': `One-pager · ${BRAND.name}`,
    '/collateral/demo-script': `Demo script · ${BRAND.name}`,
    '/collateral/enablement': `Enablement · ${BRAND.name}`,
    '/collateral/deck': `Deck · ${BRAND.name}`,
    '/collateral/video-script': `Video script · ${BRAND.name}`,
  };
  return map[path] || BRAND.product;
}
